import "./InterviewAssessment.css";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../../context/AuthContext";
import axios from "axios";
import CandidateList from "../candidatelist/CandidateList";
import { motion } from "framer-motion";
import { Card, Row, Col, Typography, Button, Space, Divider, Input } from "antd";
import { SearchOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const { Title, Text } = Typography;

export default function InterviewAssessment() {
    const { authData, backUrl } = useContext(AuthContext);

    const [mrf, setMrf] = useState([]);
    const [organizationNames, setOrganizationNames] = useState([]);
    const [departmentNames, setDepartmentNames] = useState([]);
    const [designationNames, setDesignationNames] = useState([]);
    const [showCandidates, setShowCandidates] = useState(false);
    const [currentMrfId, setCurrentMrfId] = useState();
    const [filterStatus, setFilterStatus] = useState("Open"); // 👈 status filter
    const [searchTerm, setSearchTerm] = useState(""); // 👈 new search state
    const [exporting, setExporting] = useState(false);

    // ── Tab counts: Open / Close / On-Hold ──
    const [tabCounts, setTabCounts] = useState({
        Open: 0,
        Close: 0,
        "On-Hold": 0,
    });
    const [countsLoading, setCountsLoading] = useState(false);

    const BASE_URL = backUrl;

    const fetchMrf = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/api/emp/manpowerrequisitions/?mrf_status=${filterStatus}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setMrf(res.data.results);
        } catch (err) {
            console.log("Failed to fetch MRFs:", err);
        }
    };

    // ── Fetch counts for each status tab ──
    // Hits the same endpoint per status and reads the paginated `count` field,
    // so the badges reflect the true total regardless of page size.
    const fetchTabCounts = async () => {
        if (!authData?.access) return;
        setCountsLoading(true);
        try {
            const statuses = ["Open", "Close", "On-Hold"];
            const requests = statuses.map((status) =>
                axios.get(`${BASE_URL}/api/emp/manpowerrequisitions/?mrf_status=${status}`, {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                })
            );

            const [openRes, closeRes, holdRes] = await Promise.all(requests);

            setTabCounts({
                Open: openRes.data.count ?? (openRes.data.results?.length || 0),
                Close: closeRes.data.count ?? (closeRes.data.results?.length || 0),
                "On-Hold": holdRes.data.count ?? (holdRes.data.results?.length || 0),
            });
        } catch (err) {
            console.log("Failed to fetch tab counts:", err);
        } finally {
            setCountsLoading(false);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/api/emp/organisations/`);
            setOrganizationNames(res.data.results);
        } catch (err) {
            console.log("Failed to fetch organizations:", err);
        }
    };

    const fetchDepartments = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/api/emp/departments/`);
            setDepartmentNames(res.data.results);
        } catch (err) {
            console.log("Failed to fetch departments:", err);
        }
    };

    const fetchDesignations = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/api/emp/designations/`);
            setDesignationNames(res.data.results);
        } catch (err) {
            console.log("Failed to fetch designations:", err);
        }
    };

    const getDepartmentName = (id) =>
        departmentNames.find((d) => d.id === id)?.name || "N/A";
    const getDesignationTitle = (id) =>
        designationNames.find((d) => d.id === id)?.name || "N/A";
    const getOrganizationName = (id) =>
        organizationNames.find((o) => o.id === id)?.name || "N/A";

    useEffect(() => {
        fetchMrf();
        fetchOrganizations();
        fetchDepartments();
        fetchDesignations();
        fetchTabCounts();
    }, [filterStatus]);

    // 👇 filter MRFs based on selected status button
    const statusFilteredMrf = mrf.filter((item) => {
        if (filterStatus === "All") return true;
        if (filterStatus === "Open") return item.mrf_status === "Open";
        if (filterStatus === "Closed") return item.mrf_status === "Close";
        if (filterStatus === "Hold") return item.mrf_status === "On-hold";
        return true;
    });

    // 👇 further filter by search term (designation, department, organisation, MRF number)
    const filteredMrf = statusFilteredMrf.filter((item) => {
        if (!searchTerm.trim()) return true;

        const term = searchTerm.trim().toLowerCase();
        const designation = getDesignationTitle(item.designation).toLowerCase();
        const department = getDepartmentName(item.department).toLowerCase();
        const organisation = getOrganizationName(item.organisation).toLowerCase();
        const mrfNumber = (item.mrf_number || "").toLowerCase();

        return (
            designation.includes(term) ||
            department.includes(term) ||
            organisation.includes(term) ||
            mrfNumber.includes(term)
        );
    });

    // ── Excel Export ──
    // Exports exactly what's currently visible (respects the active status tab
    // AND the search box), same pattern used elsewhere in the app.
    const handleExportToExcel = () => {
        if (!filteredMrf.length) {
            toast.info("No records to export for the current filters.");
            return;
        }

        setExporting(true);
        try {
            const rows = filteredMrf.map((item, idx) => ({
                "S.No": idx + 1,
                "MRF Number": item.mrf_number || "-",
                "Organization": getOrganizationName(item.organisation),
                "Department": getDepartmentName(item.department),
                "Designation": getDesignationTitle(item.designation),
                "Vacancies": item.no_of_vacancies ?? "-",
                "Status": item.mrf_status === "Close" ? "Closed" : (item.mrf_status || "-"),
            }));

            const worksheet = XLSX.utils.json_to_sheet(rows);

            // Reasonable column widths
            const colWidths = Object.keys(rows[0]).map((key) => ({
                wch: Math.max(
                    key.length,
                    ...rows.map((r) => String(r[key] ?? "").length)
                ) + 3,
            }));
            worksheet["!cols"] = colWidths;

            const workbook = XLSX.utils.book_new();
            const sheetName = (filterStatus || "MRF").replace(/[\\/*?:[\]]/g, "").slice(0, 31);
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || "Data");

            const dateStamp = new Date().toISOString().slice(0, 10);
            const fileName = `Open_Positions_${filterStatus}_${dateStamp}.xlsx`;

            XLSX.writeFile(workbook, fileName);
            toast.success(`Exported ${rows.length} record(s) to ${fileName}`);
        } catch (err) {
            console.error(err);
            toast.error("Failed to export data to Excel.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <>
            {!showCandidates ? (
                <motion.div
                    className="container interview-assessment-container my-4"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <ToastContainer position="top-right" autoClose={3000} />

                    <div style={{ marginBottom: 16 }}>
                        <center>
                            <Title level={3} style={{ margin: 0, color: "#0e497b" }}>
                                Open Positions
                            </Title>

                            {/* 👇 Search Box */}
                            <div style={{ marginTop: 16, maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>
                                <Input
                                    allowClear
                                    size="large"
                                    placeholder="Search by designation, department, organisation or MRF number"
                                    prefix={<SearchOutlined style={{ color: "#0e497b" }} />}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* 👇 Filter Buttons with counts */}
                            <Space style={{ marginTop: 16 }} wrap>
                                <Button
                                    style={{
                                        backgroundColor: filterStatus === "Open" ? "#0e497b" : "#fff",
                                        color: filterStatus === "Open" ? "#fff" : "#0e497b",
                                        border: "1px solid #0e497b",
                                        fontWeight: "bold",
                                    }}
                                    onClick={() => setFilterStatus("Open")}
                                >
                                    Open{" "}
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minWidth: 20,
                                            height: 20,
                                            padding: "0 6px",
                                            borderRadius: 10,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            marginLeft: 6,
                                            backgroundColor: filterStatus === "Open" ? "rgba(255,255,255,0.25)" : "#0e497b",
                                            color: "#fff",
                                        }}
                                    >
                                        {countsLoading ? "…" : tabCounts.Open}
                                    </span>
                                </Button>

                                <Button
                                    style={{
                                        backgroundColor: filterStatus === "Close" ? "#0e497b" : "#fff",
                                        color: filterStatus === "Close" ? "#fff" : "#0e497b",
                                        border: "1px solid #0e497b",
                                        fontWeight: "bold",
                                    }}
                                    onClick={() => setFilterStatus("Close")}
                                >
                                    Closed{" "}
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minWidth: 20,
                                            height: 20,
                                            padding: "0 6px",
                                            borderRadius: 10,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            marginLeft: 6,
                                            backgroundColor: filterStatus === "Close" ? "rgba(255,255,255,0.25)" : "#0e497b",
                                            color: "#fff",
                                        }}
                                    >
                                        {countsLoading ? "…" : tabCounts.Close}
                                    </span>
                                </Button>

                                <Button
                                    style={{
                                        backgroundColor: filterStatus === "On-Hold" ? "#0e497b" : "#fff",
                                        color: filterStatus === "On-Hold" ? "#fff" : "#0e497b",
                                        border: "1px solid #0e497b",
                                        fontWeight: "bold",
                                    }}
                                    onClick={() => setFilterStatus("On-Hold")}
                                >
                                    Hold{" "}
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minWidth: 20,
                                            height: 20,
                                            padding: "0 6px",
                                            borderRadius: 10,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            marginLeft: 6,
                                            backgroundColor: filterStatus === "On-Hold" ? "rgba(255,255,255,0.25)" : "#0e497b",
                                            color: "#fff",
                                        }}
                                    >
                                        {countsLoading ? "…" : tabCounts["On-Hold"]}
                                    </span>
                                </Button>

                                <Button
                                    icon={<FileExcelOutlined />}
                                    loading={exporting}
                                    onClick={handleExportToExcel}
                                    style={{
                                        backgroundColor: "#1D6F42",
                                        borderColor: "#1D6F42",
                                        color: "#fff",
                                        fontWeight: "bold",
                                    }}
                                >
                                    Download Excel
                                </Button>
                            </Space>
                        </center>
                    </div>

                    {filteredMrf.length > 0 ? (
                        <Row gutter={[24, 24]}>
                            {filteredMrf.map((mrfItem, index) => (
                                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                                    <motion.div
                                        whileHover={{
                                            scale: 1.03,
                                            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                                        }}
                                        whileTap={{ scale: 0.97 }}
                                        transition={{ type: "spring", stiffness: 300 }}
                                    >
                                        <Card
                                            hoverable
                                            bordered
                                            style={{
                                                borderRadius: 12,
                                                minHeight: 200,
                                                background: "#f9fbff",
                                            }}
                                            actions={[
                                                <Button
                                                    block
                                                    onClick={() => {
                                                        setCurrentMrfId(mrfItem.mrf_number);
                                                        setShowCandidates(true);
                                                    }}
                                                    style={{
                                                        width: "fit-content",
                                                        backgroundColor: "#0c3e6a",
                                                        color: "white",
                                                    }}
                                                >
                                                    View Candidates
                                                </Button>,
                                            ]}
                                        >
                                            <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                                <Text strong style={{ fontSize: 16 }}>
                                                    {getDesignationTitle(mrfItem.designation)}
                                                </Text>
                                                <Text type="secondary">{getDepartmentName(mrfItem.department)}</Text>
                                                <Divider style={{ margin: "8px 0" }} />
                                                <Text>
                                                    <strong>MRF Number:</strong> {mrfItem.mrf_number}
                                                </Text>
                                                <Text>
                                                    <strong>Organization:</strong> {getOrganizationName(mrfItem.organisation)}
                                                </Text>
                                                <Text>
                                                    <strong>Vacancies:</strong> {mrfItem.no_of_vacancies}
                                                </Text>
                                                <Text>
                                                    <strong>Status:</strong> {mrfItem.mrf_status === "Close" ? "Closed" : mrfItem.mrf_status}
                                                </Text>
                                            </Space>
                                        </Card>
                                    </motion.div>
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        <Text type="secondary" className="d-block text-center mt-4">
                            {searchTerm.trim()
                                ? `No positions found matching "${searchTerm}".`
                                : "No positions found."}
                        </Text>
                    )}
                </motion.div>
            ) : (
                <div id="interview-assessment-area">
                    <CandidateList
                        mrf={currentMrfId}
                        onBack={() => setShowCandidates(false)}
                        showCandidates={showCandidates}
                    />
                </div>
            )}
        </>
    );
}